/**
 * TrainingScene — the OPTIONAL "Training Grounds" (CLAUDE.md pillar 6 + the triple
 * contract). A tired player never has to open it; a player who *wants* to make their
 * team powerful can grind here on their own terms.
 *
 * What it is: a cozy practice yard reached from the tavern. The player picks an
 * intensity, then runs repeated, quick, NO-STAKES practice waves against a scaling
 * practice foe (a "training dummy" mirrored from the player's own team species, so the
 * sprites/moves always exist in the dataset). Each won wave pours GENEROUS whole-team
 * EXP through the same battle engine + leveling pipeline normal play uses — only here
 * the EXP is cranked up so deliberate grinding feels worth it.
 *
 * Pressure-free by construction:
 *  - Never required for progress; purely opt-in.
 *  - No stakes: it's a `'tower'`/practice battle — no catching, no money/item loss; a
 *    loss just ends the session warmly and you head home, exactly like a wild wipe.
 *  - Leave any time (a "Back to town" control is always reachable), and you keep every
 *    level you earned on the way.
 *
 * The renderer owns NO rules: it only assembles the practice opponent (faithful
 * instances via the live GameSession's data) and hands the Battle scene a launch
 * payload. All EXP/leveling/evolution still flows through core/ via the Battle scene.
 */
import Phaser from 'phaser';
import { COLORS, CSS, FONT, RADIUS, SPACING } from './ui/theme.js';
import { Button } from './ui/Button.js';
import { Menu, type MenuItem } from './ui/Menu.js';
import { DialogBox } from './ui/DialogBox.js';
import { Layout } from './layout/index.js';
import { fadeIn, goTo, isReducedMotion } from './flow.js';
import { peekSession } from './session.js';
import { REGISTRY_DATA_INDEX } from './PreloadScene.js';
import {
  BENCH_GROUPS,
  applyBenchItem,
  displayName,
  type BenchItem,
  type BenchEvolution,
} from './itemBench.js';
import {
  evolutionChoiceLabels,
  isEvolutionCancelKey,
  evolvedMessage,
  stoppedEvolvingMessage,
  evolvingPromptMessage,
} from './evolutionChoice.js';
import type { GameSession } from '@/session';
import type { DataIndex } from '@/data';
import type { PokemonInstance, SpeciesData, MoveSlot } from '@/shared';
import { LEVEL_MIN, LEVEL_MAX } from '@/shared';
import { computeStats, expForLevel, getNature, makeRng, hashString } from '@/core';

/**
 * Launch / re-entry contract. The Battle scene returns here (returnScene: 'Training')
 * carrying back the chosen intensity + a wave counter so the grind LOOPS without the
 * player re-picking each time — and a `lastWaveWon` flag so we can show a warm beat.
 */
export interface TrainingSceneData {
  /** The chosen practice-foe level (clamped to [LEVEL_MIN, LEVEL_MAX]). */
  readonly foeLevel?: number;
  /** Short label of the chosen intensity (for the header). */
  readonly intensityLabel?: string;
  /** How many waves have been run this visit (for the warm running tally). */
  readonly waveCount?: number;
  /** Set by the Battle scene's return data: did the player win the last wave? */
  readonly lastWaveWon?: boolean;
}

/** One intensity option the player can pick (a foe-level band relative to the team). */
interface Intensity {
  readonly key: string;
  readonly label: string;
  /** Levels added to the team's top level to set the practice foe's level. */
  readonly delta: number;
  /** A one-line cozy description. */
  readonly blurb: string;
}

/**
 * Generosity dial for Training waves: even more rewarding than normal play (the whole
 * point of grinding is a brisk power climb), but still finite. Passed to the Battle
 * scene, which forwards it to expGainFromBattle as the per-battle generosity override.
 */
const TRAINING_EXP_GENEROSITY = 3.5;

export class TrainingScene extends Phaser.Scene {
  private layout!: Layout;
  private session: GameSession | null = null;
  private index!: DataIndex;
  private reduced = false;
  private busy = false;

  private params: TrainingSceneData = {};

  private heading?: Phaser.GameObjects.Text;
  private subheading?: Phaser.GameObjects.Text;
  private note?: Phaser.GameObjects.Text;
  private menu?: Menu;
  private homeButton?: Button;
  private panel?: Phaser.GameObjects.Graphics;

  // ── the Item Bench overlay (an optional, unlimited-supply item give) ──────────
  /** The whole bench modal (scrim + panel + title + a Menu + a DialogBox). */
  private benchOverlay?: Phaser.GameObjects.Container;
  /** The active sub-menu inside the bench (item picker, then party picker). */
  private benchMenu?: Menu;
  /** The confirmation message box, shown after an item is applied. */
  private benchDialog?: DialogBox;
  /** Title/blurb texts inside the bench panel (refreshed per step). */
  private benchTitle?: Phaser.GameObjects.Text;
  private benchBlurb?: Phaser.GameObjects.Text;
  /** The item the player chose, awaiting a party target. */
  private benchItem: BenchItem | null = null;
  /** Space/Enter advances the confirmation box (keyboard parity with tap/click). */
  private benchDialogKeys?: (e: KeyboardEvent) => void;
  /**
   * The "press B to keep" listener active only while the evolution CANCEL choice is open.
   * Held so re-entry / teardown can remove it (mirrors benchDialogKeys).
   */
  private benchEvoKeys?: (e: KeyboardEvent) => void;
  /**
   * The "keep it" resolver for the open evolution CANCEL choice, so a scrim tap (which
   * bypasses the menu's own onCancel) still resolves the choice to "Keep as is". Cleared
   * once the choice settles.
   */
  private benchEvoCancel?: () => void;
  /**
   * The evolution-flourish glow: a soft white radial that pulses behind the message box
   * while a Rare Candy evolution plays out, then fades. Held so re-entry can kill it.
   */
  private evoGlow?: Phaser.GameObjects.Graphics;
  /** The glow's pulse tween (killed on teardown / re-entry so nothing lingers). */
  private evoGlowTween?: Phaser.Tweens.Tween;

  constructor() {
    super('Training');
  }

  init(data: Partial<TrainingSceneData>): void {
    this.params = {
      ...(data.foeLevel !== undefined ? { foeLevel: data.foeLevel } : {}),
      ...(data.intensityLabel !== undefined ? { intensityLabel: data.intensityLabel } : {}),
      ...(data.waveCount !== undefined ? { waveCount: data.waveCount } : {}),
      ...(data.lastWaveWon !== undefined ? { lastWaveWon: data.lastWaveWon } : {}),
    };
    this.busy = false;
  }

  create(): void {
    // Phaser REUSES the scene instance, so a re-entry (Training → Battle → Training)
    // re-runs create(). Drop every prior reference so nothing stacks (mirrors the
    // pattern the other feature scenes use).
    this.resetState();

    this.cameras.main.setBackgroundColor(COLORS.bg);
    this.layout = Layout.attach(this);
    this.reduced = isReducedMotion();
    this.session = peekSession(this);
    this.index =
      (this.registry.get(REGISTRY_DATA_INDEX) as DataIndex | undefined) ?? this.sessionIndex();

    this.panel = this.add.graphics();

    const name = this.session?.trainerName ?? 'friend';
    this.heading = this.add
      .text(0, 0, 'Training Grounds', {
        fontFamily: FONT.family,
        fontSize: `${FONT.size.lg}px`,
        color: CSS.text,
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    this.subheading = this.add
      .text(0, 0, this.subheadingText(name), {
        fontFamily: FONT.family,
        fontSize: `${FONT.size.sm}px`,
        color: CSS.textMuted,
        align: 'center',
        wordWrap: { width: 520 },
      })
      .setOrigin(0.5, 0);

    this.note = this.add
      .text(0, 0, this.noteText(), {
        fontFamily: FONT.family,
        fontSize: `${FONT.size.xs}px`,
        color: CSS.textFaint,
        align: 'center',
        wordWrap: { width: 520 },
      })
      .setOrigin(0.5, 0);

    this.buildMenu();

    this.homeButton = new Button(this, 0, 0, {
      label: 'Back to town',
      variant: 'ghost',
      width: 200,
      reducedMotion: this.reduced,
      onClick: () => this.goHome(),
    });

    this.relayout();
    this.layout.on('change', () => this.relayout());
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.teardown());
    fadeIn(this);
  }

  // ── the intensity picker (the grind loop's hub) ──────────────────────────────

  private buildMenu(): void {
    const items: MenuItem[] = this.intensities().map((it) => ({
      label: it.label,
      onSelect: () => this.beginWave(it),
    }));
    // The optional Item Bench: an unlimited supply of Rare Candy + vitamins to lavish
    // on the team. Purely opt-in power growth — a tired player simply never picks it.
    items.push({
      label: 'Item Bench  ▸',
      hint: 'free',
      onSelect: () => this.openBench(),
    });
    this.menu = new Menu(this, 0, 0, {
      reducedMotion: this.reduced,
      width: Math.min(360, this.layout.safe.width - SPACING.lg * 2),
      items,
      // Esc backs out to Town from the intensity picker (keyboard parity with Back).
      onCancel: () => this.goHome(),
    });
    this.menu.setDepth(10);
    this.menu.focus();
  }

  /**
   * The three intensity bands, scaled off the player's strongest team member. Always
   * gentle: even "Tough" stays close to the team so a practice wave is never a wall.
   */
  private intensities(): readonly Intensity[] {
    const top = this.teamTopLevel();
    const clamp = (lvl: number): number => Math.max(LEVEL_MIN, Math.min(LEVEL_MAX, lvl));
    return [
      {
        key: 'cozy',
        label: `Warm-up  (Lv ${clamp(top - 2)})`,
        delta: -2,
        blurb: 'A gentle dummy — easy, comfortable XP.',
      },
      {
        key: 'steady',
        label: `Steady  (Lv ${clamp(top)})`,
        delta: 0,
        blurb: 'An even match — a brisk, satisfying climb.',
      },
      {
        key: 'tough',
        label: `Tough  (Lv ${clamp(top + 3)})`,
        delta: 3,
        blurb: 'A sterner dummy — the richest XP if you can win.',
      },
    ];
  }

  /** Start one practice wave: build the foe, hand off to the Battle scene. */
  private beginWave(intensity: Intensity): void {
    if (this.busy) return;
    const session = this.session;
    if (!session) {
      this.goHome();
      return;
    }
    const foeLevel = Math.max(
      LEVEL_MIN,
      Math.min(LEVEL_MAX, this.teamTopLevel() + intensity.delta),
    );
    const party = this.buildPracticeFoe(foeLevel);
    if (party.length === 0) {
      // No team yet (shouldn't happen post-new-game) — keep it warm, just bow out.
      this.note?.setText('Catch a partner first, then come back to train together!');
      return;
    }

    this.busy = true;
    void session.save();

    const nextWave = (this.params.waveCount ?? 0) + 1;
    // A no-stakes practice wave (BattleSceneData shape). 'tower' kind: catching is
    // blocked upstream (core/battle), EXP still flows (kind !== 'friendly'), and the
    // cranked generosity makes deliberate grinding feel worth it.
    goTo(this, 'Battle', {
      data: {
        kind: 'tower',
        opponentParty: party,
        opponentName: 'Practice Dummy',
        returnScene: 'Training',
        returnData: {
          foeLevel,
          intensityLabel: intensity.label,
          waveCount: nextWave,
        },
        trainingExpGenerosity: TRAINING_EXP_GENEROSITY,
      },
    });
  }

  /**
   * A scaling practice foe — a "training dummy" mirrored from the player's OWN team
   * species (so the species, moves and sprites are guaranteed present), rebuilt at the
   * chosen level with full HP and faithful stats. Deterministic given (seed, level,
   * species) so the same wave is reproducible. Single foe — quick, repeatable rounds.
   */
  private buildPracticeFoe(level: number): PokemonInstance[] {
    const session = this.session;
    if (!session) return [];
    const team = session.getTeam();
    if (team.length === 0) return [];

    // Cycle the basis species by the wave count so successive dummies vary a little.
    const wave = this.params.waveCount ?? 0;
    const basis = team[wave % team.length]!;
    const species = this.index.speciesById(basis.speciesId);
    if (!species) return [];

    const rng = makeRng(
      (session.seed ^ hashString(`training|${species.id}|${level}|${wave}`)) >>> 0,
    );
    const ivs = {
      hp: rng.int(32),
      atk: rng.int(32),
      def: rng.int(32),
      spatk: rng.int(32),
      spdef: rng.int(32),
      spe: rng.int(32),
    };
    const evs = { hp: 0, atk: 0, def: 0, spatk: 0, spdef: 0, spe: 0 };
    const nature = getNature(basis.natureId);
    const stats = computeStats(species.baseStats, ivs, evs, level, nature);

    const dummy: PokemonInstance = {
      id: `training-${species.id}-${rng.getState().toString(36)}`,
      speciesId: species.id,
      nickname: 'Dummy',
      level,
      exp: expForLevel(species.growthRate, level),
      natureId: nature.id,
      ivs,
      evs,
      abilityId: species.abilities.primary,
      moves: this.movesFor(species, level),
      currentHp: stats.hp,
      maxHp: stats.hp,
      status: 'none',
      statusTurns: 0,
      heldItemId: null,
      friendship: 70,
      shiny: false,
      gender: null,
      originalTrainer: { trainerId: 'training', trainerName: 'Training', viaTrade: false },
      obtainedAt: 0,
    };
    return [dummy];
  }

  /** The up-to-4 most-recent level-up moves a species knows by `level` (real PP). */
  private movesFor(species: SpeciesData, level: number): MoveSlot[] {
    const learnable = species.learnset
      .filter((e) => e.method === 'level-up' && (e.level ?? 1) <= level)
      .sort((a, b) => (a.level ?? 1) - (b.level ?? 1))
      .slice(-4);
    const list = learnable.length > 0 ? learnable : species.learnset.slice(0, 1);
    const slots: MoveSlot[] = [];
    for (const e of list) {
      const md = this.index.moveById(e.moveId);
      const maxPp = md?.pp ?? 5;
      slots.push({ moveId: e.moveId, pp: maxPp, maxPp });
    }
    if (slots.length === 0) slots.push({ moveId: 'tackle', pp: 35, maxPp: 35 });
    return slots;
  }

  // ── the Item Bench (an unlimited-supply give to a team member) ────────────────

  /**
   * Open the Item Bench modal: a scrim + cream panel hosting a sub-menu flow —
   * pick an item, pick a party member, see a juicy confirmation, then loop. The
   * underlying intensity menu is blurred (not destroyed) so backing out restores it.
   */
  private openBench(): void {
    if (this.busy || this.benchOverlay) return;
    if (!this.session || this.session.getTeam().length === 0) {
      this.note?.setText('Catch a partner first — then the bench is yours to spoil them.');
      return;
    }
    this.menu?.blur();
    // Hide (not just dim) everything behind the bench so the modal's text — ESPECIALLY
    // the "What? X is evolving!" evolution prompt — never visually collides with the
    // intensity menu rows / blurb beneath it. Restored in closeBench()/resetState()/teardown().
    this.setBackgroundVisible(false);

    const overlay = this.add.container(0, 0);
    overlay.setDepth(200);

    const scrim = this.add.graphics();
    scrim.fillStyle(COLORS.scrim, 0.55);
    scrim.fillRect(0, 0, this.layout.width, this.layout.height);
    scrim.setInteractive(
      new Phaser.Geom.Rectangle(0, 0, this.layout.width, this.layout.height),
      Phaser.Geom.Rectangle.Contains,
    );
    // Tapping the scrim backs out one step (mirrors Esc) — never a dead modal.
    scrim.on(Phaser.Input.Events.POINTER_DOWN, () => this.benchBack());
    overlay.add(scrim);

    this.benchTitle = this.add
      .text(0, 0, '', {
        fontFamily: FONT.family,
        fontSize: `${FONT.size.md}px`,
        color: CSS.text,
        fontStyle: 'bold',
        align: 'center',
      })
      .setOrigin(0.5, 0);
    overlay.add(this.benchTitle);

    this.benchBlurb = this.add
      .text(0, 0, '', {
        fontFamily: FONT.family,
        fontSize: `${FONT.size.xs}px`,
        color: CSS.textMuted,
        align: 'center',
      })
      .setOrigin(0.5, 0);
    overlay.add(this.benchBlurb);

    this.benchOverlay = overlay;
    this.benchItem = null;
    this.showItemPicker();
  }

  /**
   * Step 1 — choose an item, grouped into sections (a "Treats" section: Rare Candy +
   * the six vitamins; a "Stones" section: the evolution stones). Each section title is
   * a calm, non-selectable divider row; every item is unlimited.
   */
  private showItemPicker(): void {
    if (!this.benchOverlay) return;
    this.benchItem = null;
    this.benchTitle?.setText('Item Bench');

    const items: MenuItem[] = [];
    for (const group of BENCH_GROUPS) {
      // A non-selectable divider row labels the section (e.g. "— Stones —").
      items.push({ label: `— ${group.title} —`, disabled: true, onSelect: () => {} });
      for (const it of group.items) {
        items.push({ label: it.label, hint: 'free', onSelect: () => this.chooseItem(it) });
      }
    }
    this.swapBenchMenu(items);
    // A calm overview under the title; each item's own one-line blurb rides its row.
    this.benchBlurb?.setText('Pick a treat or a stone — every item is unlimited.');
  }

  /** The player picked an item — advance to the party picker. */
  private chooseItem(item: BenchItem): void {
    this.benchItem = item;
    this.showPartyPicker(item);
  }

  /** Step 2 — choose which team Pokémon receives the item (name + level rows). */
  private showPartyPicker(item: BenchItem): void {
    if (!this.benchOverlay || !this.session) return;
    const team = this.session.getTeam();
    this.benchTitle?.setText(`Give ${item.label} to…`);
    this.benchBlurb?.setText(item.blurb);

    const items: MenuItem[] = team.map((mon, i) => ({
      label: displayName(mon, this.index.speciesById(mon.speciesId)),
      hint: `Lv ${mon.level}`,
      onSelect: () => this.giveItem(item, i),
    }));
    this.swapBenchMenu(items);
  }

  /**
   * Apply the chosen item to the chosen party member (mutating the LIVE working-copy
   * instance via core/items through itemBench), persist via the session, then show a
   * juicy DialogBox confirmation. On done, loop back to the item picker so the player
   * keeps spoiling the team until they back out.
   */
  private giveItem(item: BenchItem, teamIndex: number): void {
    if (!this.session || !this.benchOverlay) return;
    const team = this.session.getTeam();
    const mon = team[teamIndex];
    if (!mon) {
      this.showItemPicker();
      return;
    }
    const species = this.index.speciesById(mon.speciesId);
    if (!species) {
      this.showItemPicker();
      return;
    }

    // getTeam() returns the live array, so mutating `mon` mutates the working copy the
    // session persists. itemBench applies via core/items + auto-learns into empty slots,
    // and (passing the species lookup) evolves the mon if a candy crosses its threshold.
    const result = applyBenchItem(
      item,
      mon,
      species,
      (id) => this.index.moveById(id),
      (id) => this.index.speciesById(id),
    );

    if (result.evolution) {
      // Do NOT swap yet — the leveled-up (un-evolved) `mon` is already in the live team,
      // so cancelling is simply "don't swap." The swap (replaceTeamMember) happens only if
      // the player chooses to EVOLVE inside playBenchEvolution. Persist the level-up now so
      // it stands either way.
      void this.session.save();
      this.playBenchEvolution(mon, species, result.pages, result.evolution);
      return;
    }

    void this.session.save();
    this.showBenchMessage(result.pages);
  }

  /**
   * The Rare-Candy EVOLUTION FLOURISH, Training-Grounds flavored — now with a calm CANCEL
   * point. Unlike the Battle scene there's no on-field combatant sprite here, so the bench
   * glows instead. The sequence:
   *   (any "grew to Lv N!" / "learned X!" lines first)
   *   "What? <Name> is evolving!"
   *   → a no-pressure choice: Evolve / Keep as is
   *       EVOLVE → swap the evolved instance into the team, a soft white glow pulses, then
   *                "<Name> evolved into <NewName>!" (the juicy payoff).
   *       KEEP   → no swap (the leveled-up, un-evolved mon stays), then
   *                "<Name> stopped evolving." (the candy level-up still stands).
   * The level-up was already persisted in {@link giveItem}; the swap + a final save happen
   * here only on EVOLVE. Respects reduced motion (no pulse; the lines still show). On done
   * it loops back to the item picker like any give.
   */
  private playBenchEvolution(
    mon: PokemonInstance,
    fromSpecies: SpeciesData,
    pages: readonly string[],
    evolution: BenchEvolution,
  ): void {
    const fromName = displayName(mon, fromSpecies);

    // itemBench appends "<Name> evolved into <NewName>!" as the final page; lift it out so
    // we can frame it with the classic "What? <Name> is evolving!" prelude. Whatever came
    // before it (the level-up + any auto-learned moves) still shows, unchanged.
    const lead = pages.slice(0, Math.max(0, pages.length - 1));
    const promptPages = [...lead, evolvingPromptMessage(fromName)];

    // Show the lead + "is evolving!" prompt; when it's dismissed, offer the choice. The
    // handoff flag stops the auto-loop-back so the choice menu isn't instantly clobbered.
    this.showBenchMessage(
      promptPages,
      () => this.askBenchEvolutionChoice(mon, evolution),
      /*handoff*/ true,
    );
  }

  /**
   * The no-pressure CANCEL gate at the evolving moment (Training bench flavor). Shows a
   * small classic selection box inside the bench overlay — "Evolve" / "Keep as is" —
   * usable by TOUCH (tap a row) and KEYBOARD (↑/↓, Enter/Space to confirm, Esc OR B to
   * keep). EVOLVE swaps the evolved instance in, glows, and shows the payoff line; KEEP
   * leaves the leveled-up un-evolved mon and shows "<Name> stopped evolving." Either way
   * it loops back to the item picker afterward.
   */
  private askBenchEvolutionChoice(mon: PokemonInstance, evolution: BenchEvolution): void {
    const overlay = this.benchOverlay;
    if (!overlay || !this.session) {
      // The bench was torn down mid-prompt — keep the current form, nothing more to do.
      return;
    }
    const fromName = displayName(mon, this.index.speciesById(mon.speciesId));
    const toName = evolution.toSpecies.name;

    this.benchTitle?.setText(`${fromName} is evolving!`);
    this.benchBlurb?.setText('Let it evolve, or keep it as it is — your call.');

    let settled = false;
    // The classic "B" cancel the owner asked for (Esc is handled by the menu's onCancel).
    const bKeyHandler = (e: KeyboardEvent): void => {
      if (isEvolutionCancelKey(e.key) && e.key !== 'Escape') {
        e.preventDefault();
        choose(false);
      }
    };
    const choose = (evolve: boolean): void => {
      if (settled) return;
      settled = true;
      if (typeof window !== 'undefined') {
        window.removeEventListener('keydown', bKeyHandler);
      }
      if (this.benchEvoKeys === bKeyHandler) this.benchEvoKeys = undefined;
      this.benchEvoCancel = undefined;
      if (evolve) {
        this.session!.replaceTeamMember(mon.id, evolution.evolvedInstance);
        void this.session!.save();
        this.startEvoGlow();
        this.showBenchMessage([evolvedMessage(fromName, toName)], () => this.stopEvoGlow());
      } else {
        this.showBenchMessage([stoppedEvolvingMessage(fromName)]);
      }
    };

    this.benchEvoKeys = bKeyHandler;
    this.benchEvoCancel = () => choose(false);
    if (typeof window !== 'undefined') {
      window.addEventListener('keydown', bKeyHandler);
    }
    const labels = evolutionChoiceLabels(toName);
    this.swapBenchMenu(
      [
        { label: labels.evolve, onSelect: () => choose(true) },
        { label: labels.keep, onSelect: () => choose(false) },
      ],
      () => choose(false), // Esc on the choice = keep it (not back out of the bench)
    );
  }

  /**
   * Begin the evolution glow: a soft white radial centered on the message box that breathes
   * in/out until {@link stopEvoGlow}. Reduced motion skips the tween entirely (a static,
   * faint wash) so the moment is still marked without animation. Idempotent.
   */
  private startEvoGlow(): void {
    const overlay = this.benchOverlay;
    if (!overlay) return;
    this.stopEvoGlow();

    const cx = this.layout.centerX;
    const cy = this.layout.centerY;
    const r = Math.min(220, Math.max(140, this.layout.safe.width * 0.4));

    // Draw the radial at full fill alpha; the OBJECT alpha (set + tweened below) is what
    // breathes the bloom in and out. A few stacked rings fake a soft falloff toward the edge.
    const glow = this.add.graphics();
    glow.fillStyle(COLORS.boxFillInner, 0.32);
    glow.fillCircle(cx, cy, r);
    glow.fillCircle(cx, cy, r * 0.7);
    glow.fillCircle(cx, cy, r * 0.42);
    glow.setBlendMode(Phaser.BlendModes.ADD);
    // Sit just below the message box (depth 10) but above the title/blurb/scrim.
    glow.setDepth(5);
    overlay.add(glow);
    this.evoGlow = glow;

    if (this.reduced) {
      glow.setAlpha(0.5);
      return;
    }
    glow.setAlpha(0.15);
    this.evoGlowTween = this.tweens.add({
      targets: glow,
      alpha: 0.75,
      yoyo: true,
      repeat: -1,
      duration: 520,
      ease: 'Sine.easeInOut',
    });
  }

  /** Fade + destroy the evolution glow (and stop its pulse). Idempotent + safe. */
  private stopEvoGlow(): void {
    this.evoGlowTween?.remove();
    this.evoGlowTween = undefined;
    const glow = this.evoGlow;
    this.evoGlow = undefined;
    if (!glow) return;
    if (this.reduced || !this.scene.isActive()) {
      glow.destroy();
      return;
    }
    this.tweens.add({
      targets: glow,
      alpha: 0,
      duration: 260,
      ease: 'Sine.easeIn',
      onComplete: () => glow.destroy(),
    });
  }

  /**
   * Show the confirmation pages in a DialogBox; on done, return to the item picker.
   * `onClosed` (optional) fires once the box is dismissed — used by the evolution
   * flourish to fade out its glow exactly when the celebration finishes.
   *
   * `handoff: true` means `onClosed` itself drives the NEXT step (e.g. it presents the
   * evolution CANCEL choice) — so we must NOT auto-loop back to the item picker, which
   * would instantly clobber whatever onClosed put up.
   */
  private showBenchMessage(pages: readonly string[], onClosed?: () => void, handoff = false): void {
    const overlay = this.benchOverlay;
    if (!overlay) {
      onClosed?.();
      return;
    }
    this.benchMenu?.blur();
    this.benchMenu?.destroy();
    this.benchMenu = undefined;
    this.benchTitle?.setText('');
    this.benchBlurb?.setText('');

    const safe = this.layout.safe;
    const w = Math.min(440, safe.width - SPACING.lg * 2);
    const h = Math.min(180, Math.max(120, safe.height * 0.34));
    const dialog = new DialogBox(this, this.layout.centerX, this.layout.centerY, {
      width: w,
      height: h,
      reducedMotion: this.reduced,
      onDone: () => {
        this.clearBenchDialogKeys();
        dialog.destroy();
        if (this.benchDialog === dialog) this.benchDialog = undefined;
        onClosed?.();
        // Loop back so the player can keep giving items until they back out — UNLESS
        // onClosed took over the UI (the evolution choice), which drives its own next step.
        if (!handoff && this.benchOverlay) this.showItemPicker();
      },
    });
    dialog.setDepth(10);
    dialog.show([...pages]);
    overlay.add(dialog);
    this.benchDialog = dialog;

    // Keyboard parity: Space/Enter advances the box exactly like a tap/click does.
    this.benchDialogKeys = (e: KeyboardEvent) => {
      if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        this.benchDialog?.tap();
      }
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('keydown', this.benchDialogKeys);
    }
    this.relayout();
  }

  /** Remove the confirmation-box keyboard listener (idempotent). */
  private clearBenchDialogKeys(): void {
    if (this.benchDialogKeys && typeof window !== 'undefined') {
      window.removeEventListener('keydown', this.benchDialogKeys);
    }
    this.benchDialogKeys = undefined;
  }

  /** Remove the evolution-choice "press B to keep" listener + resolver (idempotent). */
  private clearBenchEvoKeys(): void {
    if (this.benchEvoKeys && typeof window !== 'undefined') {
      window.removeEventListener('keydown', this.benchEvoKeys);
    }
    this.benchEvoKeys = undefined;
    this.benchEvoCancel = undefined;
  }

  /**
   * Back out one step of the bench flow: from the party picker → the item picker;
   * from the item picker → close the whole bench. A confirmation box swallows the
   * back gesture (the player taps it to advance instead). Idempotent + safe.
   */
  private benchBack(): void {
    if (!this.benchOverlay) return;
    if (this.benchDialog) return; // the dialog owns the next tap
    // While the evolution CANCEL choice is open, a back gesture (Esc/scrim tap) means
    // "keep it" — route through the choice's own resolver so the form is kept, the
    // "stopped evolving" beat shows, and the B-key listener is cleaned up.
    if (this.benchEvoCancel) {
      this.benchEvoCancel();
      return;
    }
    if (this.benchItem) {
      this.showItemPicker();
      return;
    }
    this.closeBench();
  }

  /** Tear down the whole bench modal and re-focus the intensity menu. */
  private closeBench(): void {
    this.clearBenchDialogKeys();
    this.clearBenchEvoKeys();
    this.stopEvoGlow();
    this.benchMenu?.destroy();
    this.benchMenu = undefined;
    this.benchDialog?.destroy();
    this.benchDialog = undefined;
    this.benchTitle = undefined;
    this.benchBlurb = undefined;
    this.benchItem = null;
    this.benchOverlay?.destroy(true);
    this.benchOverlay = undefined;
    // Re-reveal the intensity menu + its labels/blurb now that nothing sits over them.
    this.setBackgroundVisible(true);
    this.menu?.focus();
  }

  /**
   * Toggle the visibility of everything that lives BEHIND the bench overlay — the card
   * panel, heading/subheading, the intensity Menu, the blurb/note, and the Back button.
   * The bench opens over them, so we HIDE them (rather than relying on the scrim's dim)
   * to stop the modal's prompt text from colliding with the intensity rows. Guarded so
   * a missing object (mid-teardown) is simply skipped — safe to call from restore paths.
   */
  private setBackgroundVisible(visible: boolean): void {
    this.panel?.setVisible(visible);
    this.heading?.setVisible(visible);
    this.subheading?.setVisible(visible);
    this.menu?.setVisible(visible);
    this.note?.setVisible(visible);
    this.homeButton?.setVisible(visible);
  }

  /**
   * Replace the bench's active sub-menu with a fresh {@link Menu} of `items`, wired
   * for keyboard (Esc backs out), touch, and mouse. Destroys the prior sub-menu first
   * so nothing stacks. `onCancel` overrides the default back gesture (the evolution
   * CANCEL choice uses this so Esc means "keep it" rather than backing out of the bench).
   */
  private swapBenchMenu(items: MenuItem[], onCancel?: () => void): void {
    const overlay = this.benchOverlay;
    if (!overlay) return;
    this.benchMenu?.blur();
    this.benchMenu?.destroy();

    const menu = new Menu(this, 0, 0, {
      reducedMotion: this.reduced,
      width: Math.min(360, this.layout.safe.width - SPACING.lg * 2),
      items,
      onCancel: onCancel ?? (() => this.benchBack()),
    });
    menu.setDepth(10);
    overlay.add(menu);
    this.benchMenu = menu;
    menu.focus();
    this.relayout();
  }

  // ── helpers ──────────────────────────────────────────────────────────────────

  private teamTopLevel(): number {
    const team = this.session?.getTeam() ?? [];
    let top = LEVEL_MIN;
    for (const m of team) if (m.level > top) top = m.level;
    return top;
  }

  /** The cozy reassurance line + a one-liner per intensity (keeps `blurb` visible). */
  private noteText(): string {
    const lines = this.intensities().map((it) => `${it.label.split('  ')[0]}: ${it.blurb}`);
    return (
      'Practice is optional and no-stakes — grind as much (or as little) as you like.\n' +
      'A loss just ends the session, and you keep every level you earn.\n' +
      lines.join('   ')
    );
  }

  private subheadingText(name: string): string {
    const waves = this.params.waveCount ?? 0;
    if (waves > 0) {
      const won = this.params.lastWaveWon;
      const tally = `${waves} wave${waves === 1 ? '' : 's'} done`;
      if (won === true) return `Nicely done, ${name}! (${tally}) Run another, or head home.`;
      if (won === false)
        return `Good effort, ${name}. (${tally}) Catch your breath — go again any time.`;
      return `Welcome back, ${name}. (${tally}) Pick an intensity to spar again.`;
    }
    return `Spar a practice dummy to level your team, ${name}. Choose how hard.`;
  }

  private goHome(): void {
    if (this.busy) return;
    this.busy = true;
    if (this.session) void this.session.save();
    goTo(this, 'Town');
  }

  private sessionIndex(): DataIndex {
    // The Battle scene resolves the DataIndex off the registry; if we somehow run
    // before Preload set it, fall back to the session's own bundle index via a probe.
    const probe = this.session as unknown as { bundle?: { index?: DataIndex } } | null;
    const idx = probe?.bundle?.index;
    if (idx) return idx;
    throw new Error('TrainingScene: no DataIndex available (Preload must run first).');
  }

  // ── layout + lifecycle ─────────────────────────────────────────────────────────

  private relayout(): void {
    const safe = this.layout.safe;
    const cx = this.layout.centerX;

    const cardW = Math.min(560, safe.width - SPACING.lg * 2);
    const cardX = cx - cardW / 2;

    // MEASURE-TO-CONTENT: first re-wrap every paragraph to the ACTUAL card width so the
    // measured heights below reflect how many lines the text takes on THIS viewport (a
    // ~390px phone wraps to more lines than desktop). Then we flow the stack and size the
    // card to fit — so the note can never overlap the menu buttons or the Back control.
    const textWrap = cardW - SPACING.lg * 2;
    this.subheading?.setWordWrapWidth(textWrap);
    this.note?.setWordWrapWidth(textWrap);

    const headH = this.heading?.height ?? FONT.size.lg;
    const subH = this.subheading?.height ?? FONT.size.sm;
    const menuH = this.menu?.boxHeight ?? 0;
    const noteH = this.note?.height ?? FONT.size.xs * 3;
    const homeH = this.homeButton?.buttonHeight ?? 48;

    // Total content height = the flowed stack with its gaps + the Back button below it.
    const contentH =
      SPACING.lg +
      headH +
      SPACING.sm +
      subH +
      SPACING.md +
      menuH +
      SPACING.lg +
      noteH +
      SPACING.lg +
      homeH +
      SPACING.lg;
    // Grow the card to fit content (the common tall-phone case), capped to the safe area;
    // on a genuinely tiny screen the ScrollList-free card simply uses all the height it has.
    const cardH = Math.min(safe.height - SPACING.md * 2, Math.max(contentH, 200));
    const cardY = safe.y + Math.max(SPACING.md, (safe.height - cardH) / 2);

    if (this.panel) {
      this.panel.clear();
      this.panel.fillStyle(COLORS.scrim, 0.12);
      this.panel.fillRoundedRect(cardX + 3, cardY + 5, cardW, cardH, RADIUS.lg);
      this.panel.fillStyle(COLORS.surface, 1);
      this.panel.fillRoundedRect(cardX, cardY, cardW, cardH, RADIUS.lg);
      this.panel.lineStyle(2, COLORS.boxBorder, 1);
      this.panel.strokeRoundedRect(cardX, cardY, cardW, cardH, RADIUS.lg);
    }

    // Flow the stack top→bottom; the Back button follows the note (never pinned under it),
    // so wrapped text can never sit on an interactive control no matter the line count.
    let y = cardY + SPACING.lg;
    this.heading?.setPosition(cx, y);
    y += headH + SPACING.sm;
    this.subheading?.setPosition(cx, y);
    y += subH + SPACING.md;

    if (this.menu) {
      this.menu.setPosition(cx, y + menuH / 2);
      y += menuH + SPACING.lg;
    }

    this.note?.setPosition(cx, y);
    y += noteH + SPACING.lg;

    this.homeButton?.setPosition(cx, y + homeH / 2);

    this.layoutBench();
  }

  /**
   * Center the Item Bench modal (title + blurb + the active sub-menu) over the scrim.
   * Called from {@link relayout} so a viewport change / step swap re-flows the panel.
   * The DialogBox (when shown) is self-centered, so the title/blurb just clear out.
   */
  private layoutBench(): void {
    if (!this.benchOverlay) return;
    const cx = this.layout.centerX;
    const cy = this.layout.centerY;

    // While a confirmation box is up, the title/blurb are blanked and the dialog owns
    // the center — nothing else to position.
    if (this.benchDialog) return;

    const titleH = this.benchTitle?.height ?? FONT.size.md;
    const blurbH = this.benchBlurb?.height ?? FONT.size.xs;
    const menuH = this.benchMenu?.boxHeight ?? 0;
    const stackH = titleH + SPACING.sm + blurbH + SPACING.md + menuH;

    let y = cy - stackH / 2;
    this.benchTitle?.setPosition(cx, y);
    y += titleH + SPACING.sm;
    this.benchBlurb?.setPosition(cx, y);
    y += blurbH + SPACING.md;
    this.benchMenu?.setPosition(cx, y + menuH / 2);
  }

  private resetState(): void {
    this.clearBenchDialogKeys();
    this.clearBenchEvoKeys();
    // Re-entry safety: if the bench had hidden the background, restore it before we drop
    // the references, so a surviving object can never be left stuck-hidden on re-entry.
    this.setBackgroundVisible(true);
    this.heading = undefined;
    this.subheading = undefined;
    this.note = undefined;
    this.menu = undefined;
    this.homeButton = undefined;
    this.panel = undefined;
    this.benchOverlay = undefined;
    this.benchMenu = undefined;
    this.benchDialog = undefined;
    this.benchTitle = undefined;
    this.benchBlurb = undefined;
    this.benchItem = null;
    this.evoGlow = undefined;
    this.evoGlowTween = undefined;
    this.busy = false;
  }

  private teardown(): void {
    this.tweens.killAll();
    // Tear the bench modal down first (its sub-menu + dialog have live key listeners).
    this.clearBenchDialogKeys();
    this.clearBenchEvoKeys();
    this.evoGlowTween = undefined;
    this.evoGlow?.destroy();
    this.evoGlow = undefined;
    this.benchMenu?.destroy();
    this.benchDialog?.destroy();
    this.benchOverlay?.destroy(true);
    this.menu?.destroy();
    this.homeButton?.destroy();
    this.heading?.destroy();
    this.subheading?.destroy();
    this.note?.destroy();
    this.panel?.destroy();
    this.resetState();
  }
}
